import { AGENT_EVENTS } from "../../utils/agent-utils";
import { getAudioTTSOpenAI } from "../../utils/openAI-utils";
import aiAgents, { Agent } from "../AIAgents";

class AudioManager {
  private sessionAudioChunks: Array<Uint8Array> = [];
  private lastAudioUrl: string | null = null;
  public currentAgent: Agent;
  public isPlaying: boolean = false;

  private audioContext: AudioContext | null = null;
  private analyserMap: Map<string, AnalyserNode> = new Map();
  private dataArrayMap: Map<string, Uint8Array> = new Map();
  private mediaSourceNodes = new Map<string, MediaElementAudioSourceNode>();

  // Queue to hold fetched audio URLs for playback
  private playbackQueue: Array<{
    audioUrl: string;
    audioBlob: any;
    resolve: () => void;
    reject: (error: any) => void;
    autoplay: boolean;
    initialAudio: boolean;
    finalAudio: boolean;
  }> = [];

  private getOrCreateMediaElementSource(agentId: string, audioElement: HTMLAudioElement): MediaElementAudioSourceNode {
    let source = this.mediaSourceNodes.get(agentId);
    if (!source) {
      if (this.audioContext) {
        source = this.audioContext.createMediaElementSource(audioElement);
        this.mediaSourceNodes.set(agentId, source);
      } else {
        throw new Error("AudioContext is not initialized.");
      }
    }
    return source;
  }

  private setupAnalyser(agent: Agent, audioElement: HTMLAudioElement) {
    if (!this.audioContext) {
      this.audioContext = new AudioContext();
    }

    const source = this.getOrCreateMediaElementSource(agent.id, audioElement);

    const analyser = this.audioContext.createAnalyser();
    analyser.fftSize = 32;

    source.connect(analyser);
    analyser.connect(this.audioContext.destination);

    this.analyserMap.set(agent.id, analyser);
    this.dataArrayMap.set(agent.id, new Uint8Array(analyser.frequencyBinCount));

    this.measureAmplitudeLoop(agent, audioElement);
  }

  private measureAmplitudeLoop(agent: Agent, audioElement: HTMLAudioElement) {
    const analyser = this.analyserMap.get(agent.id);
    const dataArray = this.dataArrayMap.get(agent.id);

    if (!analyser || !dataArray) return;

    const getAmplitude = () => {
      analyser.getByteTimeDomainData(dataArray);
      let sum = 0;
      for (let i = 0; i < dataArray.length; i++) {
        const amplitude = (dataArray[i] - 128) / 128;
        sum += amplitude * amplitude;
      }
      const rawVolume = Math.sqrt(sum / dataArray.length);
      return rawVolume;
    };

    const measure = () => {
      if (audioElement.paused || audioElement.ended) {
        return;
      }

      const amplitude = getAmplitude();

      aiAgents.dispatchAgentTalking(agent, amplitude);

      requestAnimationFrame(measure);
    };

    requestAnimationFrame(measure);
  }

  public async playStoredAudio(agent: Agent, audioBuffer: Blob, timestamp?: number): Promise<void> {
    // const combinedBuffer = this.concatenateBuffers(audioBuffer);
    // const audioBlob = new Blob([combinedBuffer], { type: 'audio/mpeg' });
    const audioUrl = URL.createObjectURL(audioBuffer);

    if (this.lastAudioUrl === audioUrl) {
      if (timestamp !== undefined) {
        agent.currentAudio.currentTime = timestamp;
      }
      await agent.currentAudio.play();
    } else {
      agent.currentAudio.src = audioUrl;
      if (timestamp !== undefined) {
        agent.currentAudio.currentTime = timestamp;
      }

      this.setupAnalyser(agent, agent.currentAudio);

      await agent.currentAudio.play();
      this.lastAudioUrl = audioUrl;
    }
  }

  public async playAudioBase64(agent: Agent, audioBase64: string): Promise<void> {
    const audioBlob = this.base64ToBlob(audioBase64, "audio/mpeg");
    const audioUrl = URL.createObjectURL(audioBlob);

    if (this.lastAudioUrl === audioBase64) {
      await agent.currentAudio.play();
    } else {
      agent.currentAudio.src = audioUrl;

      this.setupAnalyser(agent, agent.currentAudio);

      await agent.currentAudio.play();
      this.lastAudioUrl = audioBase64;
    }
  }

  public playStreamedAudio = async ({
    signal,
    agent,
    prompt,
    autoplay,
    initialAudio,
    finalAudio,
    bufferKey,
  }: {
    signal?: AbortSignal;
    agent: Agent;
    prompt: string;
    autoplay: boolean;
    initialAudio?: boolean;
    finalAudio?: boolean;
    bufferKey: number;
  }): Promise<void> => {
    try {
      // Fetch the TTS audio data with the AbortSignal for cancellation
      const response = await getAudioTTSOpenAI({ uuid: agent.objectUuid, prompt, signal });

      if (!response.body) {
        console.error("Failed to fetch audio stream");
        throw new Error("No response body in audio stream");
      }

      const reader = response.body.getReader();
      const audioChunks: Uint8Array[] = [];

      // Read and collect audio chunks
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        if (value) {
          audioChunks.push(value);
          this.sessionAudioChunks.push(value); // Store the raw chunk for later
        }
      }

      // Create a Blob from the collected chunks
      const audioBlob = new Blob(audioChunks, { type: "audio/mpeg" });
      const audioUrl = URL.createObjectURL(audioBlob);

      // Update agent.audioBuffers based on initialAudio and bufferKey
      if (initialAudio) {
        agent.audioBuffers[bufferKey] = audioBlob;
      } else if (agent.audioBuffers) {
        const lastBuffer = agent.audioBuffers[bufferKey];
        if (lastBuffer) {
          agent.audioBuffers[bufferKey] = lastBuffer.concat(audioBlob);
        } else {
          agent.audioBuffers[bufferKey] = audioBlob;
        }
      }
      aiAgents.updateAgent({ ...agent, audioBuffers: agent.audioBuffers });

      // Return a Promise that resolves when playback ends or rejects on error
      await new Promise<void>((resolve, reject) => {
        // Start playback if autoplay is enabled and is in current tab
        if (autoplay && document.hasFocus()) {
          // Create an Audio element and set its source
          const audioElement = this.currentAgent.currentAudio || new Audio();
          audioElement.src = audioUrl;

          this.setupAnalyser(agent, audioElement);

          // if chunk will be activated move to onended
          window.APP.messageDispatch.dispatchEvent(new CustomEvent("ai-cancel-play"));
          resolve();

          const handlePlaying = () => {
            aiAgents.dispatchStartSpeaking(agent);
          };

          audioElement.addEventListener('playing', handlePlaying);

          // Handle playback end
          audioElement.onended = () => {
            setTimeout(() => {
              aiAgents.handleFinishEndAudio(agent);
            }, 500);
            URL.revokeObjectURL(audioUrl); // Clean up the Blob URL
            audioElement.removeEventListener('playing', handlePlaying); // Clean up listener
          };

          // Handle playback errors
          audioElement.onerror = error => {
            console.error("Error playing audio:", error);
            window.dispatchEvent(new CustomEvent(AGENT_EVENTS.AI_FINISH_SPEAK));
            URL.revokeObjectURL(audioUrl); // Clean up the Blob URL
            reject(error);
          };

          const playPromise = audioElement.play();
          if (playPromise !== undefined) {
            playPromise.catch(error => {
              console.error("Playback failed:", error);
              window.dispatchEvent(new CustomEvent(AGENT_EVENTS.AI_FINISH_SPEAK));
              URL.revokeObjectURL(audioUrl); // Clean up the Blob URL
              reject(error);
            });
          }
        } else {
          // If not autoplay, resolve immediately after setting up the audio
          resolve();
          setTimeout(() => {
            if (autoplay) {
              window.APP.messageDispatch.dispatchEvent(new CustomEvent("ai-cancel-play"));
            }
          }, 10);
        }
      });
    } catch (error) {
      console.error("Error during audio streaming:", error);
      window.dispatchEvent(new CustomEvent(AGENT_EVENTS.AI_FINISH_SPEAK));
      throw error;
    }
  };

  /**
   * Play streamed audio by fetching it and enqueueing for playback.
   * Returns a Promise that resolves when playback is complete.
   */
  // public playStreamedAudioQueue = async ({
  //   signal,
  //   agent,
  //   prompt,
  //   autoplay,
  //   initialAudio,
  //   finalAudio,
  //   bufferKey
  // }: {
  //   signal: AbortSignal;
  //   agent: Agent;
  //   prompt: string;
  //   autoplay: boolean;
  //   initialAudio?: boolean;
  //   finalAudio?: boolean;
  //   bufferKey: number;
  // }): Promise<void> => {
  //   return new Promise(async (resolve, reject) => {
  //     try {
  //       // Fetch the TTS audio data
  //       const response = await getAudioTTSOpenAI({ apiKey: agent?.apiKey, prompt, signal });

  //       if (!response.body) {
  //         console.error('Failed to fetch audio stream');
  //         reject(new Error('No response body in audio stream'));
  //         return;
  //       }

  //       const reader = response.body.getReader();
  //       const audioChunks: Uint8Array[] = [];

  //       // Read and collect audio chunks
  //       while (true) {
  //         const { done, value } = await reader.read();
  //         if (done) break;
  //         if (value) {
  //           audioChunks.push(value);
  //           this.sessionAudioChunks.push(value); // Store the raw chunk for later
  //         }
  //       }

  //       // Create a Blob from the collected chunks
  //       const audioBlob = new Blob(audioChunks, { type: 'audio/mpeg' });
  //       if (initialAudio) {
  //           agent.audioBuffers?.push(audioBlob);
  //       } else if (agent.audioBuffers && agent.audioBuffers.length > 0) {
  //           const lastBuffer = agent.audioBuffers[bufferKey];
  //           if (lastBuffer) {
  //             // agent.audioBuffers[bufferKey] = lastBuffer.concat(audioChunks);
  //           } else if(agent.audioBuffers[bufferKey] === undefined) {
  //             agent.audioBuffers.push(audioBlob);
  //           } else {
  //             agent.audioBuffers[bufferKey] = audioBlob
  //           }
  //       }
  //       const audioUrl = URL.createObjectURL(audioBlob);

  //       // Enqueue the audio URL for playback
  //       this.playbackQueue.push({ audioUrl, resolve, reject, audioBlob, autoplay, initialAudio: initialAudio || false, finalAudio: finalAudio || false });

  //       // Start playback if not already playing
  //       if (!this.isPlaying) {
  //         this.processPlaybackQueue();
  //       }

  //     } catch (error) {
  //       // console.error('Error during audio streaming:', error);
  //       // alert("Couldn't process audio of Agent, please try again");
  //       window.dispatchEvent(new CustomEvent(AGENT_EVENTS.AI_FINISH_SPEAK));
  //       reject(error);
  //     }
  //   });
  // };

  /**
   * Process the playback queue sequentially.
   */
  private async processPlaybackQueue(): Promise<void> {
    if (this.isPlaying || this.playbackQueue.length === 0) {
      return;
    }

    this.isPlaying = true;

    const { audioUrl, resolve, reject, audioBlob, autoplay, finalAudio, initialAudio } = this.playbackQueue.shift()!;

    const audioElement = this.currentAgent.currentAudio || new Audio();
    audioElement.src = audioUrl;

    if (!autoplay) {
      this.isPlaying = false;
      resolve();
    } else {
      // Handle playback end

      audioElement.onended = () => {
        this.isPlaying = false;
        if (finalAudio) {
          window.APP.messageDispatch.dispatchEvent(new CustomEvent("ai-cancel-play"));
          window.APP.messageDispatch.dispatchEvent(new CustomEvent(AGENT_EVENTS.AI_FINISH_SPEAK));

          if (this.currentAgent) {
            this.currentAgent?.el.dispatchEvent(new CustomEvent(AGENT_EVENTS.AI_FINISH_SPEAK));
          }
        }

        // Revoke the Blob URL to free memory
        URL.revokeObjectURL(audioUrl);

        // Continue with the next audio in the queue
        this.processPlaybackQueue();
      };
      // Resolve the Promise associated with this audio
      resolve();
    }

    // Handle playback errors
    audioElement.onerror = error => {
      console.error("Error playing audio:", error);
      this.isPlaying = false;
      window.APP.messageDispatch.dispatchEvent(new CustomEvent(AGENT_EVENTS.AI_FINISH_SPEAK));
      reject(error);
      // Continue with the next audio in the queue
      this.processPlaybackQueue();
    };

    // this.currentAgent = this.getAgentByIdFromPlayback(audioUrl); // Implement this method if needed

    // Start playback
    try {
      if (autoplay) {
        await audioElement.play();
      }
    } catch (error) {
      console.error("Error playing audio:", error);
      this.isPlaying = false;
      reject(error);
      // Continue with the next audio in the queue
      this.processPlaybackQueue();
    }
  }

  private concatenateBuffers(buffers: Uint8Array[]): ArrayBuffer {
    const totalLength = buffers.reduce((sum, buffer) => sum + buffer.byteLength, 0);
    const combinedBuffer = new Uint8Array(totalLength);
    let offset = 0;

    buffers.forEach(buffer => {
      combinedBuffer.set(buffer, offset);
      offset += buffer.byteLength;
    });

    return combinedBuffer.buffer;
  }

  private base64ToBlob(base64: string, type: string): Blob {
    const binary = atob(base64);
    const array = Uint8Array.from(binary, char => char.charCodeAt(0));
    return new Blob([array], { type });
  }

  public clearSessionChunks() {
    this.sessionAudioChunks = [];
  }
}

export default AudioManager;
